import { createSlice } from '@reduxjs/toolkit';

// A music-info public id is a base62 STRING. Numeric values are internal row
// ids that must never be written into a *_public_id / URL — see
// utils/entityId.js::publicEntityId for the full rationale.
const cleanPublicId = (v) => (
    typeof v === 'string' && v.trim() && !/^\d+$/.test(v.trim()) ? v.trim() : null
);

const DJPAL_SNAPSHOT_KEYS = [
    'activeSearch',
    'artistQuery',
    'artistResults',
    'trackQuery',
    'trackResults',
    'trackVariant',
    'trackVariantCache',
    'mixQuery',
    'mixResults',
    'mixResultsPagination',
    'mixFilterContext',
    'mixPagePublicId',
    'artistPagePublicId',
    'trackPagePublicId',
    'expandedMixIds',
    'searchAllQuery',
    'searchAllScope',
    'trackListQuery',
    'trackListResults',
    'trackListPagination',
    'trackListContext',
    'artistsGridContext',
    'labelResults',
];

const djpalSlice = createSlice({
    name: 'djpal',
    initialState: {
        token: localStorage.getItem('djpal:token') || '',
        tokenValid: null,
        tokenError: null,
        activeSearch: 'artist',
        // True between "go home" (clearResults) and the next search start.
        // While set, the setXxxResults reducers drop non-null payloads:
        // a search that was still in flight when the user went home would
        // otherwise land late and repopulate the page — with its queries
        // already cleared, that rendered a half-empty page (or yanked the
        // user straight back off the home). Transient — never snapshotted.
        suppressLateResults: false,
        isSearching: false,
        searchError: null,
        dateRange: null,
        // Artist search
        artistQuery: '',
        artistResults: null,
        // Set from the click payload when an artist search starts, so the
        // page can paint a real header (name + artwork) while results load.
        // Shape: { name, artworkUrl } | null.
        pendingArtist: null,
        // Track search
        trackQuery: '',
        trackResults: null,
        // Click-payload artwork for the pending track header (see pendingArtist).
        // Shape: { name, artworkUrl } | null.
        pendingTrack: null,
        // Click-payload of a mix opened from a card, so its page skeleton has
        // the real cover, title and DJ. Shape: { title, dj, artwork } | null.
        pendingMix: null,
        trackVariant: 'A',
        trackVariantCache: {},
        // Mix search
        mixQuery: '',
        mixResults: null,
        mixResultsPagination: null,
        // Describes the current mix-results page header when filtered by
        // something other than a title (e.g. "Mixes by X", "Featuring X").
        // Shape: { kind: 'by' | 'featuring', artistName: string } | null.
        mixFilterContext: null,
        // Public id of the mix whose detail page is open, recorded when the
        // page is opened BY id (the normal click path) rather than derived
        // from results. It has to survive a failed fetch: the URL writer needs
        // the id even when the request died offline, or the refresh that
        // follows has nothing but lossy slugs to search by.
        mixPagePublicId: null,
        // Same idea for the artist and track pages.
        artistPagePublicId: null,
        trackPagePublicId: null,
        expandedMixIds: [],
        playingMix: null,
        // Unified search (web navbar → results page)
        searchAllQuery: '',
        // Scope pill on the unified results page ('all'|'artists'|'tracks'|
        // 'mixes'). Lives in the slice (not component state) so pill clicks
        // participate in Back/Forward via the nav-history snapshots.
        searchAllScope: 'all',
        // Paginated track list (Show all tracks → /tracks?q=...)
        trackListQuery: '',
        trackListResults: null,
        trackListPagination: null,
        // Describes the current track-list view when it's NOT a free-text
        // search — drives endpoint selection in the search/load-more thunks
        // and the header copy in TrackListSearch.
        // Shape: { kind: 'playedBy', artistName, artistPublicId } | null.
        trackListContext: null,
        // Full-page artists grid ('artistsGrid' view) — the homepage-style
        // avatar+Follow grid opened from a card's external-link icon.
        // Shape: { title, subtitle, artists: [{ name, id, artworkUrl }] } | null.
        artistsGridContext: null,
        // Label page ('label' view). Shape: { publicId, name, pending?,
        // error?, ...label, tracks: [track], totalTracks, page, limit } | null.
        // `pending` paints the header while the fetch is in flight; the full
        // payload replaces it (openMixpediaLabel in appNavThunk).
        labelResults: null,
    },
    reducers: {
        setToken: (state, action) => {
            state.token = action.payload;
        },
        setTokenValid: (state, action) => {
            state.tokenValid = action.payload;
        },
        setTokenError: (state, action) => {
            state.tokenError = action.payload;
        },
        setActiveSearch: (state, action) => {
            state.activeSearch = action.payload;
            // Errors from one search type shouldn't leak into another tab.
            state.searchError = null;
            // Every navigation entry point (goArtist/goMix/goTrack/navbar/
            // drill-downs) routes through here before its results land —
            // a new destination means late results are wanted again.
            state.suppressLateResults = false;
        },
        setIsSearching: (state, action) => {
            state.isSearching = action.payload;
            // Search thunks all mark the start this way (covers re-searches
            // that don't change activeSearch).
            if (action.payload) state.suppressLateResults = false;
        },
        setSearchError: (state, action) => {
            state.searchError = action.payload;
        },
        setDateRange: (state, action) => {
            state.dateRange = action.payload;
        },
        setArtistQuery: (state, action) => {
            state.artistQuery = action.payload;
        },
        setArtistResults: (state, action) => {
            if (state.suppressLateResults && action.payload != null) return;
            state.artistResults = action.payload;
        },
        setPendingArtist: (state, action) => {
            state.pendingArtist = action.payload;
        },
        setPendingMix: (state, action) => {
            state.pendingMix = action.payload;
        },
        setPendingTrack: (state, action) => {
            state.pendingTrack = action.payload;
        },
        appendArtistMixes: (state, action) => {
            if (!state.artistResults) return;
            const { section, mixes, pagination } = action.payload;
            if (section === 'byArtist') {
                state.artistResults.mixesByArtist = [...(state.artistResults.mixesByArtist || []), ...mixes];
                if (pagination) state.artistResults.paginationByArtist = pagination;
            } else if (section === 'including') {
                state.artistResults.mixesIncludingArtist = [...(state.artistResults.mixesIncludingArtist || []), ...mixes];
                if (pagination) state.artistResults.paginationIncluding = pagination;
            }
        },
        setTrackQuery: (state, action) => {
            state.trackQuery = action.payload;
        },
        setTrackResults: (state, action) => {
            if (state.suppressLateResults && action.payload != null) return;
            state.trackResults = action.payload;
        },
        setTrackVariant: (state, action) => {
            state.trackVariant = action.payload;
        },
        setTrackVariantCache: (state, action) => {
            const { variant, data } = action.payload;
            state.trackVariantCache[variant] = data;
        },
        clearTrackVariantCache: (state) => {
            state.trackVariantCache = {};
        },
        setMixQuery: (state, action) => {
            state.mixQuery = action.payload;
        },
        setMixPagePublicId: (state, action) => {
            state.mixPagePublicId = cleanPublicId(action.payload);
        },
        setArtistPagePublicId: (state, action) => {
            state.artistPagePublicId = cleanPublicId(action.payload);
        },
        setTrackPagePublicId: (state, action) => {
            state.trackPagePublicId = cleanPublicId(action.payload);
        },
        setMixResults: (state, action) => {
            if (state.suppressLateResults && action.payload != null) return;
            state.mixResults = action.payload;
            if (action.payload != null) state.pendingMix = null;
            // Single result: expanded from the FIRST paint. The old
            // after-paint auto-expand in MixSearch flashed a collapsed frame
            // (artwork resized mid-flight) before the expanded layout.
            if (Array.isArray(action.payload) && action.payload.length === 1 && action.payload[0]?.id != null) {
                const id = action.payload[0].id;
                if (!state.expandedMixIds.includes(id)) state.expandedMixIds.push(id);
            }
        },
        setMixResultsPagination: (state, action) => {
            state.mixResultsPagination = action.payload;
        },
        appendMixResults: (state, action) => {
            // Same guard as appendArtistMixes: a load-more that resolves
            // after the list was cleared must not resurrect it from null.
            if (!state.mixResults) return;
            const { mixes, pagination } = action.payload;
            state.mixResults = [...state.mixResults, ...mixes];
            // A later page may not carry the event's DJ count (artistCount): keep page 1's, or the hero's "19 DJs" vanishes on scroll.
            if (pagination) state.mixResultsPagination = { ...pagination, artistCount: pagination.artistCount ?? state.mixResultsPagination?.artistCount };
        },
        updateMixTracklist: (state, action) => {
            const { mixId, tracklist } = action.payload;
            if (!Array.isArray(state.mixResults)) return;
            const idx = state.mixResults.findIndex(m => m.id === mixId);
            if (idx === -1) return;
            state.mixResults[idx] = { ...state.mixResults[idx], tracklist };
        },
        setMixFilterContext: (state, action) => {
            state.mixFilterContext = action.payload;
        },
        toggleExpandedMix: (state, action) => {
            const id = action.payload;
            if (state.expandedMixIds.includes(id)) {
                state.expandedMixIds = state.expandedMixIds.filter(x => x !== id);
            } else {
                state.expandedMixIds.push(id);
            }
        },
        clearExpandedMixIds: (state) => {
            state.expandedMixIds = [];
        },
        setPlayingMix: (state, action) => {
            state.playingMix = action.payload;
        },
        setSearchAllQuery: (state, action) => {
            state.searchAllQuery = action.payload;
        },
        setSearchAllScope: (state, action) => {
            state.searchAllScope = action.payload;
        },
        setTrackListQuery: (state, action) => {
            state.trackListQuery = action.payload;
        },
        setTrackListResults: (state, action) => {
            if (state.suppressLateResults && action.payload != null) return;
            state.trackListResults = action.payload;
        },
        setTrackListPagination: (state, action) => {
            state.trackListPagination = action.payload;
        },
        appendTrackListResults: (state, action) => {
            // Same guard as appendArtistMixes: a load-more that resolves
            // after the list was cleared must not resurrect it from null.
            if (!state.trackListResults) return;
            const { tracks, pagination } = action.payload;
            state.trackListResults = [...state.trackListResults, ...tracks];
            if (pagination) state.trackListPagination = pagination;
        },
        setTrackListContext: (state, action) => {
            state.trackListContext = action.payload;
        },
        setArtistsGridContext: (state, action) => {
            state.artistsGridContext = action.payload;
        },
        setLabelResults: (state, action) => {
            if (state.suppressLateResults && action.payload != null) return;
            state.labelResults = action.payload;
        },
        appendLabelTracks: (state, action) => {
            // Same guard as appendArtistMixes: a load-more that resolves
            // after the page was cleared must not resurrect it from null.
            if (!state.labelResults) return;
            const { tracks, page, totalTracks } = action.payload;
            state.labelResults.tracks = [...(state.labelResults.tracks || []), ...(tracks || [])];
            if (page != null) state.labelResults.page = page;
            if (totalTracks != null) state.labelResults.totalTracks = totalTracks;
        },
        clearResults: (state) => {
            state.artistResults = null;
            state.pendingArtist = null;
            state.pendingMix = null;
            state.pendingTrack = null;
            state.trackResults = null;
            state.mixResults = null;
            state.mixResultsPagination = null;
            state.mixFilterContext = null;
            state.mixPagePublicId = null;
            state.artistPagePublicId = null;
            state.trackPagePublicId = null;
            state.expandedMixIds = [];
            state.searchError = null;
            state.trackVariantCache = {};
            state.trackListResults = null;
            state.trackListPagination = null;
            state.trackListContext = null;
            state.artistsGridContext = null;
            state.labelResults = null;
            // Also clear the page queries so the Mixpedia shell reads as
            // "nothing open" and shows the home rows again.
            state.artistQuery = '';
            state.trackQuery = '';
            state.mixQuery = '';
            state.trackListQuery = '';
            state.searchAllQuery = '';
            state.searchAllScope = 'all';
            state.activeSearch = 'artist';
            // Drop any search still in flight — see suppressLateResults.
            state.suppressLateResults = true;
        },
        clearToken: (state) => {
            state.token = '';
            state.tokenValid = null;
            state.tokenError = null;
            localStorage.removeItem('djpal:token');
        },
        restoreDjpalSnapshot: (state, action) => {
            const snap = action.payload || {};
            for (const key of DJPAL_SNAPSHOT_KEYS) {
                if (key in snap) state[key] = snap[key];
            }
            // Back/forward restores results on purpose — never suppress them.
            state.suppressLateResults = false;
        },
    },
});

export const {
    setToken,
    setTokenValid,
    setTokenError,
    setActiveSearch,
    setIsSearching,
    setSearchError,
    setDateRange,
    setArtistQuery,
    setArtistResults,
    setPendingArtist,
    setPendingTrack,
    appendArtistMixes,
    setTrackQuery,
    setTrackResults,
    setTrackVariant,
    setTrackVariantCache,
    clearTrackVariantCache,
    setMixQuery,
    setMixResults,
    setPendingMix,
    setMixPagePublicId,
    setArtistPagePublicId,
    setTrackPagePublicId,
    setMixResultsPagination,
    appendMixResults,
    updateMixTracklist,
    setMixFilterContext,
    toggleExpandedMix,
    clearExpandedMixIds,
    setPlayingMix,
    setSearchAllQuery,
    setSearchAllScope,
    setTrackListQuery,
    setTrackListResults,
    setTrackListPagination,
    appendTrackListResults,
    setTrackListContext,
    setArtistsGridContext,
    setLabelResults,
    appendLabelTracks,
    clearResults,
    clearToken,
    restoreDjpalSnapshot,
} = djpalSlice.actions;

export { DJPAL_SNAPSHOT_KEYS };

// True when no Mixpedia page is open — the shell shows the home rows.
// Single source of truth shared by DJPalTab (view routing) and the navbar
// home button (active/filled state). Query OR results counts as "open":
// some nav-restore paths repopulate results without re-setting the query
// text. A search in flight for the ACTIVE view counts as "open" — otherwise
// clicking an artist on the home showed nothing (home stayed up, no
// spinner) until results landed. The query gate matters: a lingering
// isSearching from an abandoned page (e.g. Back pressed mid-fetch) must
// NOT keep the shell off the home, or it renders a blank view.
export const selectMixpediaViewIsEmpty = (state) => {
    const d = state.djpal;
    switch (d.activeSearch) {
        case 'all': return !d.searchAllQuery;
        case 'track': return !d.trackQuery && !d.trackResults;
        // Context-driven lists ("Tracks played by X") have an empty query
        // while fetching — without the in-flight clause the home flashed
        // for the whole fetch after clicking Show All on an artist card.
        case 'trackList': return !d.trackListQuery && !d.trackListResults && !(d.isSearching && d.trackListContext);
        case 'mix': return !d.mixQuery && !d.mixResults;
        case 'homeSets':
        case 'homeTracks':
        case 'homeArtistTracks':
        case 'homeLibraryArtists': return false;
        case 'artistsGrid': return !d.artistsGridContext;
        case 'label': return !d.labelResults;
        case 'artist':
        // A failed artist search stays on the artist page (its error banner
        // has Retry); it used to drop back to the home rows after the wait.
        default: return !d.artistResults && !((d.isSearching || d.searchError) && (d.artistQuery || d.pendingArtist));
    }
};

export default djpalSlice.reducer;
